import React, { useCallback, useState } from "react";
import { ActionPanel } from "components/panel";
import { DataDisplayRecord } from "components/data-display";
import { FormCheckbox, FormMultiSelect } from "components/form-fields";
import { Button } from "components/buttons";
import { LicenceCheckingService } from "services";
import { AlertConfirm, NotificationToaster } from "components/notifications";
import { Intent, Position } from "@blueprintjs/core";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Tooltip } from "components/tooltip/Tooltip";
import { TypesOfCoverRecord } from "pages/licence-checking/grey-fleet/shared";

const defaultArray = [];

export function ApproveVehicleRegistrationPanel({
    registration,
    checkId,
    refreshCheck,
    insurance,
    coverTypes = defaultArray,
}) {
    const greyFleetCredits = useSelector(
        (state) => state.licenceChecking.greyFleetCredits
    );
    const [isSaving, setIsSaving] = useState(false);
    const [showRejectCheckAlert, setShowRejectCheckAlert] = useState(false);
    const [ignorePolicy, setIgnorePolicy] = useState(false);
    const [requiredCoverTypeIds, setRequiredCoverTypeIds] = useState([]);

    // The check cannot be approved or rejected until the company has said what cover it requires,
    // because the requirement is recorded against the check either way.
    const hasNoRequiredCover = requiredCoverTypeIds.length === 0;

    const declaredCoverTypeIds = insurance?.coverTypeIds ?? defaultArray;

    // Mirrors lc.stp_Enroll_Grey_Fleet_Check: every required type must be among the cover the
    // driver declared. The rule only applies once the driver has declared something, matching
    // the API, which leaves legacy records that carry only the earlier business use answer alone.
    const requirementNotDeclared = declaredCoverTypeIds.length > 0
        && requiredCoverTypeIds.some((id) => !declaredCoverTypeIds.includes(id));

    const handleAction = useCallback(
        (enroll) => {
            setIsSaving(true);
            LicenceCheckingService.enrollGreyFleetCheck(
                checkId,
                enroll,
                ignorePolicy,
                requiredCoverTypeIds
            )
                .then(
                    () => {
                        NotificationToaster.show(
                            Intent.SUCCESS,
                            "Grey Fleet Check enrollment updated successfully"
                        );
                        refreshCheck();
                    },
                    (error) => {
                        // A rejected request comes back as the message the API sent; anything else
                        // rejects with the response, which has nothing worth showing.
                        if (typeof error === "string") {
                            NotificationToaster.show(Intent.WARNING, error);
                        } else {
                            NotificationToaster.show(
                                Intent.DANGER,
                                "Could not update Grey Fleet Check enrollment"
                            );
                        }
                    }
                )
                .catch(() => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not update Grey Fleet Check enrollment"
                    );
                })
                .finally(() => {
                    setIsSaving(false);
                    setShowRejectCheckAlert(false);
                });
        },
        [checkId, ignorePolicy, requiredCoverTypeIds]
    );

    const handleApprove = useCallback(() => handleAction(true), [handleAction]);
    const handleReject = useCallback(() => handleAction(false), [handleAction]);

    const onCoverTypeSelect = useCallback((item) => {
        setRequiredCoverTypeIds((previous) => {
            if (previous.includes(item.id)) {
                return previous.filter((id) => id !== item.id);
            }

            return [...previous, item.id];
        });
    }, []);

    // The tag input reports a removal using the rendered tag value, which is the name of the cover
    // type, so it has to be resolved back to an id before it can be dropped from the selection.
    const onCoverTypeRemove = useCallback(
        (name) => {
            const removedCoverType = coverTypes.find(
                (coverType) => coverType.name === name
            );

            if (removedCoverType == null) {
                return;
            }

            setRequiredCoverTypeIds((previous) =>
                previous.filter((id) => id !== removedCoverType.id)
            );
        },
        [coverTypes]
    );

    return (
        <div className="spacer-top">
            <ActionPanel intent="warning" title="Approve Vehicle Registration">
                <div>
                    <p className="spacer-bottom">
                        The vehicle registration has been submitted by your
                        employee and has not yet been approved. To go ahead with
                        the grey fleet check please approve the vehicle
                        registration below.
                    </p>
                    <DataDisplayRecord
                        header="Vehicle Registration"
                        data={registration}
                    />
                    <div className="spacer-top">
                        <TypesOfCoverRecord
                            loading={false}
                            header="Declared type(s) of cover"
                            coverTypeIds={insurance?.coverTypeIds}
                            coverTypes={coverTypes}
                            legacyBusinessUse={insurance?.businessUse}
                        />
                    </div>
                    <div className="spacer-top">
                        <FormMultiSelect
                            id="required-cover-types"
                            headingText="Required type(s) of cover"
                            placeholder="Please select"
                            items={coverTypes}
                            selectedValues={requiredCoverTypeIds.map((id) => ({ id }))}
                            onItemSelect={onCoverTypeSelect}
                            onRemove={onCoverTypeRemove}
                            disabled={isSaving}
                        />
                    </div>
                    <div className="spacer-top">
                        <FormCheckbox
                            id="ignore-policy-check"
                            label="This vehicle does not have to comply with the company policy"
                            checked={ignorePolicy}
                            onChange={(event) =>
                                setIgnorePolicy(event.target.checked)
                            }
                            disabled={isSaving}
                        />
                    </div>
                    <div>
                        <div className="bp6-dialog-footer-actions">
                            {greyFleetCredits <= 0 ? (
                                <Tooltip
                                    position={Position.TOP}
                                    content="You do not have enough credits to create a new check. Please purchase more credits to proceed."
                                >
                                    <Button
                                        id="add-question-type"
                                        onClick={handleApprove}
                                        text={"Approve"}
                                        intent="Primary"
                                        disabled
                                    />
                                </Tooltip>
                            ) : (
                                <Button
                                    id="add-question-type"
                                    onClick={handleApprove}
                                    text={"Approve"}
                                    intent="Primary"
                                    disabled={
                                        isSaving ||
                                        hasNoRequiredCover ||
                                        requirementNotDeclared
                                    }
                                />
                            )}
                            <Button
                                text="Reject Check"
                                intent="danger"
                                disabled={isSaving || hasNoRequiredCover}
                                onClick={() => setShowRejectCheckAlert(true)}
                            />
                        </div>
                    </div>
                </div>
            </ActionPanel>

            <AlertConfirm
                isOpen={showRejectCheckAlert}
                title={"Reject Check?"}
                onConfirm={handleReject}
                onCancel={() => setShowRejectCheckAlert(false)}
            >
                <p>Are you sure you want to reject this check?</p>
            </AlertConfirm>
        </div>
    );
}

ApproveVehicleRegistrationPanel.propTypes = {
    registration: PropTypes.string.isRequired,
    checkId: PropTypes.number.isRequired,
    refreshCheck: PropTypes.func.isRequired,
    insurance: PropTypes.object,
    coverTypes: PropTypes.array,
};
